import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Wine, Copy, ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function MfaSetup() {
  const { mfaSetupStart, mfaSetupVerify } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState("loading"); // loading | enroll | recovery | error
  const [error, setError] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await mfaSetupStart();
      if (r.ok) {
        setQr(r.qr_data_url);
        setSecret(r.secret);
        setPhase("enroll");
      } else {
        setError(r.error || "Couldn't start setup.");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < 6) { toast.error("Enter the 6-digit code from your app."); return; }
    setBusy(true);
    const r = await mfaSetupVerify(code);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Verification failed");
      setCode("");
      return;
    }
    setRecovery(r.recoveryCodes || []);
    setPhase("recovery");
  };

  const copyAllRecovery = async () => {
    try {
      await navigator.clipboard.writeText(recovery.join("\n"));
      toast.success("Recovery codes copied");
    } catch {
      toast.error("Copy failed — write them down manually.");
    }
  };

  return (
    <div className="min-h-screen login-canvas grain overflow-hidden">
      <div className="relative z-10 max-w-[560px] mx-auto px-4 py-12 min-h-screen flex flex-col items-center justify-center fade-in">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/literally-logo.png"
            alt="Literally Vodka"
            className="w-11 h-11 rounded-lg object-cover shadow-soft"
          />
          <div>
            <div className="font-display text-lg text-brand-ink">LiterallyDrink</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Two-factor setup</div>
          </div>
        </div>

        <div className="card p-8 sm:p-10 w-full" data-testid="mfa-setup-card">
          {phase === "loading" && <div className="text-brand-mutedFg text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Preparing your secure setup…</div>}

          {phase === "error" && (
            <div>
              <div className="label">Setup unavailable</div>
              <h2 className="font-display text-2xl text-brand-ink mt-1">Couldn't start two-factor setup</h2>
              <p className="text-sm text-brand-mutedFg mt-3">{error}</p>
              <button onClick={() => navigate("/login")} className="btn-outline mt-5">Back to sign in</button>
            </div>
          )}

          {phase === "enroll" && (
            <div>
              <div className="label flex items-center gap-1"><ShieldCheck size={12} /> Step 1 of 2</div>
              <h2 className="font-display text-3xl text-brand-ink mt-1">Set up two-factor authentication</h2>
              <p className="text-sm text-brand-mutedFg mt-2">
                Scan the QR with <strong className="text-brand-ink">Google Authenticator</strong>, <strong className="text-brand-ink">Authy</strong>, or <strong className="text-brand-ink">1Password</strong>.
                Then enter the 6-digit code below.
              </p>

              <div className="mt-5 grid sm:grid-cols-[auto,1fr] gap-5 items-center">
                <div className="rounded-2xl border border-brand-border p-3 bg-white inline-flex">
                  {qr && <img src={qr} alt="" className="w-44 h-44" data-testid="mfa-qr" />}
                </div>
                <div className="text-sm">
                  <div className="label mb-1">Can't scan?</div>
                  <div className="text-brand-mutedFg">Enter this secret manually:</div>
                  <div className="mt-2 font-mono text-xs bg-brand-muted text-brand-ink p-3 rounded-xl tracking-wider break-all" data-testid="mfa-secret">{secret}</div>
                </div>
              </div>

              <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
                <label className="block">
                  <span className="label">6-digit code</span>
                  <input
                    data-testid="mfa-setup-code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    className="input-base mt-1 tracking-[0.5em] font-mono text-center text-2xl"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    required
                  />
                </label>
                <button type="submit" disabled={busy || code.length < 6} className="btn-primary" data-testid="mfa-setup-submit">
                  {busy ? "Verifying…" : (<>Confirm <ArrowRight size={16}/></>)}
                </button>
              </form>
            </div>
          )}

          {phase === "recovery" && (
            <div>
              <div className="label flex items-center gap-1"><ShieldCheck size={12} /> Step 2 of 2 — Save these</div>
              <h2 className="font-display text-3xl text-brand-ink mt-1">Recovery codes</h2>
              <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-stage-qualifiedBg/40 border border-stage-qualifiedBg">
                <AlertTriangle size={14} className="text-stage-qualifiedFg mt-0.5 shrink-0" />
                <p className="text-xs text-stage-qualifiedFg">
                  Write these down or store them in a password manager.
                  Each code works <strong>once</strong> if you lose access to your authenticator app.
                  <strong> You won't see them again.</strong>
                </p>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm" data-testid="mfa-recovery-codes">
                {recovery.map((c) => (
                  <div key={c} className="bg-brand-muted text-brand-ink rounded-lg px-3 py-2 tracking-wider text-center">{c}</div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={copyAllRecovery} className="btn-outline flex-1" data-testid="copy-recovery">
                  <Copy size={14} /> Copy all
                </button>
                <button onClick={() => navigate("/")} className="btn-primary flex-1" data-testid="finish-mfa-setup">
                  I've saved them <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
